/**
 * Custom NumberStatsComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { map } from 'rxjs';
import { NumberStatsComponent as ParentNumberStatsComponent } from '@shared-lib/global-components/number-stats/number-stats.component';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';

@Component({
  selector: 'app-number-stats',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './number-stats.component.html',
  styleUrls: ['./number-stats.component.scss']
})
export class NumberStatsComponent extends ParentNumberStatsComponent {
  private readonly customGlobalFilterService: GlobalFilterService;
  private readonly customChartFilterHandler: ChartFilterHandlerService;

  constructor(
    configService: ConfigService,
    globalFilterService: GlobalFilterService,
    chartFilterHandler: ChartFilterHandlerService,
  ) {
    super(configService, globalFilterService, chartFilterHandler);
    this.customGlobalFilterService = globalFilterService;
    this.customChartFilterHandler = chartFilterHandler;
  }

  override ngAfterViewInit(): void {
    this.applyInputDefault();
    super.ngAfterViewInit();
  }

  get resolvedFontSize(): string {
    return String(this.data?.fontSize ?? this.data?.fontsize ?? '').trim();
  }

  override onInputStatEnter(): any {
    if (!this.isInputNumberStat) {
      return;
    }

    const key = String(this.data?.key || '').trim();
    if (!key) {
      return;
    }

    const currentFilters = this.customGlobalFilterService.getCurrentFilters();
    const customerName = currentFilters?.requestParams?.customer_name;
    if (customerName !== undefined && !Array.isArray(customerName)) {
      this.customGlobalFilterService.updateFilterSelection(
        'customer_name',
        [customerName],
        false,
        undefined,
        false,
      );
    }

    this.customChartFilterHandler.updateFilterSelection(
      { ...this.data, filterKey: key },
      { [key]: this.statInputValue },
      { [key]: this.statInputValue },
    );
  }

  private applyInputDefault(): void {
    if (!this.data?.inputNumberStat) {
      return;
    }

    const filterKey = this.data?.filterKey || this.data?.key;
    const defaultValue = this.data?.inputDefaultValue;

    if (!filterKey || defaultValue === undefined || defaultValue === null) {
      return;
    }

    const currentFilters = this.customGlobalFilterService.getCurrentFilters();
    const existingValue =
      currentFilters?.requestParams?.[filterKey] ?? currentFilters?.queryParams?.[filterKey];

    if (existingValue !== undefined) {
      return;
    }

    const isQueryParam = Boolean(this.data?.useQueryParams);
    this.customGlobalFilterService.updateFilterSelection(
      filterKey,
      [defaultValue],
      isQueryParam,
      this.data?.widgetId,
      false,
      this.data?.filterListener,
    );
  }

  override onStatInputChange(value: any): any {
    // TODO: Add your custom logic here
    return super.onStatInputChange(value);
  }

  override loadStat(filters?: any): any {
    return super.loadStat(filters).pipe(
      map((response: any) => this.applyIncludeMFormatting(response)),
    );
  }

  protected override formatValue(value: any): string {
    if (this.data?.formatMode === 'currency-thousands') {
      return this.formatCurrencyThousands(value, this.data?.prefix || '$', this.data?.suffix || 'K');
    }

    if (this.data?.useFormatStat) {
      return this.formatStatWithDecimals(value, this.data?.key || '', this.data?.prefix || '');
    }

    return super.formatValue(value);
  }

  private formatCurrencyThousands(value: any, prefix: string = '$', suffix: string = 'K'): string {
    if (value === null || value === undefined || value === '') return '-';

    const numericValue = Number(value);
    if (isNaN(numericValue)) return '-';

    const thousandsValue = numericValue / 1e3;
    const roundedValue = Math.round(thousandsValue);
    return `${prefix}${roundedValue}${suffix}`;
  }

  private formatStatWithDecimals(value: any, key: string = '', prefix: string = ''): string {
    if (value === null || value === undefined || value === '') return '-';

    const numericValue = Number(value);
    if (isNaN(numericValue)) return '-';

    const isPercentage = key.includes('%');
    if (isPercentage) {
      return `${numericValue.toFixed(1)}${prefix}`;
    }

    const absValue = Math.abs(numericValue);
    let formattedValue = '';
    let suffix = '';

    if (absValue >= 1e9) {
      formattedValue = (numericValue / 1e9).toFixed(2);
      suffix = 'B';
    } else if (absValue >= 1e6) {
      formattedValue = (numericValue / 1e6).toFixed(2);
      suffix = 'M';
    } else if (absValue >= 1e3) {
      formattedValue = (numericValue / 1e3).toFixed(2);
      suffix = 'K';
    } else {
      formattedValue = numericValue.toFixed(2);
    }

    // Remove .00 if present
    if (formattedValue.endsWith('.00')) {
      formattedValue = formattedValue.slice(0, -3);
    }

    return `${prefix}${formattedValue}${suffix}`;
  }

  private applyIncludeMFormatting(response: any): any {
    if (!this.data?.includem || !this.data?.key || this.data?.useFormatStat || this.isInputNumberStat) {
      return response;
    }

    const key = this.data.key;
    const directValue = response?.[key];
    if (directValue !== undefined) {
      return {
        ...response,
        [key]: this.formatIncludeMValue(directValue),
      };
    }

    const nestedValue = response?.responseList?.[0]?.data?.[0]?.[key];
    if (nestedValue === undefined) {
      return response;
    }

    const responseList = [...(response.responseList || [])];
    const firstResponse = { ...(responseList[0] || {}) };
    const data = [...(firstResponse.data || [])];
    data[0] = {
      ...(data[0] || {}),
      [key]: this.formatIncludeMValue(nestedValue),
    };
    firstResponse.data = data;
    responseList[0] = firstResponse;

    return {
      ...response,
      responseList,
    };
  }

  private formatIncludeMValue(value: any): string {
    const stringValue = String(value ?? '').trim();
    if (!stringValue) {
      return stringValue;
    }

    const withoutPrefix = stringValue.startsWith('$') ? stringValue.slice(1) : stringValue;
    const withoutSuffix = /m$/i.test(withoutPrefix) ? withoutPrefix.slice(0, -1) : withoutPrefix;
    return `$${withoutSuffix}M`;
  }
}
